import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { Button, Spinner } from "./ui/kit";
import { Okno } from "./SablonyDialogy";
import { fmtKc } from "../lib/format";
import api, { apiError } from "../lib/api";

/**
 * Ceny z nabídky do ceníku: řádky, jejichž nákup nebo prodejní cena v ceníku
 * chybí nebo je jiná. U každé položky jde zvlášť zaškrtnout nákup a prodej
 * („zdražilo — změnit i prodejní cenu?“). „Opravit v ceníku“ zapíše nový nákup
 * i do historie cen, „Založit do ceníku“ přidá novou položku. Vše předvybrané,
 * jedno potvrzení — další nabídka si ceny vezme z ceníku.
 *
 * `ids` omezí návrh na vybrané řádky (hromadná lišta). `dokonceni` = dotaz
 * před dokončením nabídky: `onHotovo` se zavolá po uložení i po „Dokončit bez
 * uložení“, a hned (bez okna), když není co uložit.
 */
export default function DoCenikuDialog({ vid, ids, dokonceni = false, onClose, onHotovo }) {
  const [radky, setRadky] = useState(null);
  const [nakup, setNakup] = useState({});
  const [prodej, setProdej] = useState({});
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");

  useEffect(() => {
    api.get(`/api/nabidky-verze/${vid}/do-ceniku`).then(({ data }) => {
      const r = ids ? data.radky.filter((x) => ids.includes(x.radek_id)) : data.radky;
      if (!r.length && dokonceni) { onHotovo?.(false); return; }
      setRadky(r);
      setNakup(Object.fromEntries(r.filter((x) => x.nova != null).map((x) => [x.radek_id, true])));
      setProdej(Object.fromEntries(r.filter((x) => x.prodej_nova != null).map((x) => [x.radek_id, true])));
    }).catch((e) => {
      if (dokonceni) onHotovo?.(false);          // dokončení kvůli návrhu neblokovat
      else { setRadky([]); setChyba(apiError(e.response?.data?.detail) || e.message); }
    });
  }, [vid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (radky === null) return null;
  const ids_ = radky.filter((x) => nakup[x.radek_id] && x.nova != null).map((x) => x.radek_id);
  // Nová položka bez nákupu nevznikne — prodej se u ní uloží jen spolu s nákupem.
  const prodejIds = radky.filter((x) => prodej[x.radek_id] && x.prodej_nova != null
    && (x.akce === "OPRAVIT" || nakup[x.radek_id])).map((x) => x.radek_id);
  const pocet = new Set([...ids_, ...prodejIds]).size;
  const ulozit = async () => {
    setBusy(true); setChyba("");
    try {
      await api.post(`/api/nabidky-verze/${vid}/do-ceniku`, { ids: ids_, prodej_ids: prodejIds });
      onHotovo?.(true);
    } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  const zaskrt = (zap, onChange, testid, children) => (
    <label className="tap md:min-h-0 flex items-center gap-2.5 cursor-pointer py-1" data-testid={testid}>
      <input type="checkbox" className="sr-only" checked={zap} onChange={(e) => onChange(e.target.checked)} />
      <span className={`h-5 w-5 shrink-0 rounded-md border inline-flex items-center justify-center ${zap ? "bg-ink border-ink text-white" : "border-line bg-white"}`}>
        {zap && <Check size={13} strokeWidth={3} />}
      </span>
      {children}
    </label>
  );
  const cena = (popisek, z, na) => (
    <span className="flex-1 min-w-0 flex items-baseline justify-between gap-2 t-meta">
      <span className="text-sec">{popisek}</span>
      <span className="amount whitespace-nowrap">
        {z !== undefined && <span className="text-muted">{z == null ? "bez ceny" : fmtKc(z)} → </span>}
        <b className="text-ink">{fmtKc(na)} Kč</b>
      </span>
    </span>
  );

  return (
    <Okno titulek={dokonceni ? "Uložit ceny do ceníku?" : "Uložit ceny do ceníku"} onClose={onClose} testid="do-ceniku"
      sirka="md:max-w-[600px]"
      paticka={radky.length ? <>
        {dokonceni
          ? <button onClick={() => onHotovo?.(false)} disabled={busy} className="btn-ghost tap w-full md:w-auto justify-center" data-testid="do-ceniku-preskocit">Dokončit bez uložení</button>
          : <button onClick={onClose} className="btn-ghost tap w-full md:w-auto justify-center">Zrušit</button>}
        <Button onClick={ulozit} disabled={busy || !pocet} className="w-full md:w-auto" data-testid="do-ceniku-ulozit">
          {busy ? <Spinner /> : `${dokonceni ? "Uložit a dokončit" : "Uložit do ceníku"} (${pocet})`}
        </Button>
      </> : <Button onClick={onClose} className="w-full md:w-auto">Zavřít</Button>}>
      {radky.length ? (
        <>
          <p className="t-meta text-sec">
            {dokonceni ? `${radky.length} ${radky.length === 1 ? "položka má" : radky.length < 5 ? "položky mají" : "položek má"} jiné ceny než ceník. ` : ""}
            Další nabídka si ceny vezme z ceníku. Ceny v téhle nabídce se nemění.
          </p>
          <ul className="rounded-[14px] border border-line divide-y divide-line-soft" data-testid="do-ceniku-seznam">
            {radky.map((x) => (
              <li key={x.radek_id} className="px-3.5 py-2.5" data-testid={`do-ceniku-${x.radek_id}`}>
                <div className="t-telo font-600 text-ink line-clamp-2 md:truncate">{x.nazev}</div>
                <div className="t-meta text-muted mb-0.5">
                  {x.akce === "OPRAVIT"
                    ? (x.polozka_nazev && x.polozka_nazev !== x.nazev ? `Opravit v ceníku: ${x.polozka_nazev}` : "Opravit v ceníku")
                    : `Založit do ceníku · ${x.typ === "PRACE" ? "práce" : "materiál"}`}
                </div>
                {x.nova != null && zaskrt(!!nakup[x.radek_id], (v) => setNakup({ ...nakup, [x.radek_id]: v }), `do-ceniku-nakup-${x.radek_id}`,
                  cena("Nákup", x.akce === "OPRAVIT" ? x.puvodni : undefined, x.nova))}
                {x.prodej_nova != null && zaskrt(!!prodej[x.radek_id], (v) => setProdej({ ...prodej, [x.radek_id]: v }), `do-ceniku-prodej-${x.radek_id}`,
                  cena("Prodejní cena", x.akce === "OPRAVIT" ? x.prodej_puvodni : undefined, x.prodej_nova))}
              </li>
            ))}
          </ul>
        </>
      ) : <p className="t-telo text-sec" data-testid="do-ceniku-prazdne">{chyba || "Všechny ceny z nabídky už v ceníku jsou."}</p>}
      {chyba && radky.length > 0 && <p className="t-meta text-danger">{chyba}</p>}
    </Okno>
  );
}
